import { ItemBase, ItemRarity, ItemMod, EquipmentItem, EquipmentVisual, EquipmentSlot } from '@shared/types';
import { ITEM } from '@shared/constants';
import { getItemIconPath, SLOT_FALLBACK_ICONS } from '@client/ui/ItemIcons';
import { getBaseById, getDefaultGridSize, getCategoryForSlot, LEGACY_BASE_MAP, type BaseItemDefinition } from './BaseItemRegistry';
import { rollModsForItem, getModNamePrefix, getModNameSuffix } from './ModifierPool';

// Generate unique IDs
let itemIdCounter = 0;
function generateItemId(): string {
  return `item_${Date.now()}_${itemIdCounter++}`;
}

// Item type definitions
export type ItemType = 'equipment' | 'consumable' | 'currency' | 'material';

export interface ConsumableItem extends ItemBase {
  itemType: 'consumable';
  effect: 'heal' | 'mana' | 'buff';
  value: number;
  duration?: number;
}

export interface CurrencyItem extends ItemBase {
  itemType: 'currency';
  currencyType: 'gold' | 'altun' | 'gümüş';
  stackSize: number;
  maxStack: number;
}

export interface MaterialItem extends ItemBase {
  itemType: 'material';
  materialType: string;
  stackSize: number;
  maxStack: number;
}

export type GameItem = EquipmentItem | ConsumableItem | CurrencyItem | MaterialItem | QuestItem;

// Base item templates
export const EQUIPMENT_BASES: Record<string, Omit<EquipmentItem, 'id' | 'rarity' | 'prefixes' | 'suffixes'>> = {
  // Weapons
  'kilic': {
    name: 'Kılıç',
    slot: 'weapon',
    level: 1,
    implicit: [{ type: 'damage', value: 10, tier: 1 }],
    enhancement: 0,
  },
  'balta': {
    name: 'Balta',
    slot: 'weapon',
    level: 5,
    implicit: [{ type: 'damage', value: 15, tier: 1 }],
    enhancement: 0,
  },
  'yay': {
    name: 'Yay',
    slot: 'weapon',
    level: 1,
    implicit: [{ type: 'damage', value: 8, tier: 1 }, { type: 'attackSpeed', value: 1.2, tier: 1 }],
    enhancement: 0,
  },
  'asa': {
    name: 'Asa',
    slot: 'weapon',
    level: 1,
    implicit: [{ type: 'spellDamage', value: 15, tier: 1 }],
    enhancement: 0,
  },

  // Armor
  'demir_zirh': {
    name: 'Demir Zırh',
    slot: 'chest',
    level: 5,
    implicit: [{ type: 'armor', value: 30, tier: 1 }],
    enhancement: 0,
  },
  'deri_zirh': {
    name: 'Deri Zırh',
    slot: 'chest',
    level: 1,
    implicit: [{ type: 'armor', value: 15, tier: 1 }, { type: 'evasion', value: 10, tier: 1 }],
    enhancement: 0,
  },
  'kumasli_kaftan': {
    name: 'Kumaşlı Kaftan',
    slot: 'chest',
    level: 1,
    implicit: [{ type: 'energyShield', value: 20, tier: 1 }],
    enhancement: 0,
  },

  // Helmets
  'demir_tolgasi': {
    name: 'Demir Tolgası',
    slot: 'head',
    level: 5,
    implicit: [{ type: 'armor', value: 15, tier: 1 }],
    enhancement: 0,
  },
  'börk': {
    name: 'Börk',
    slot: 'head',
    level: 1,
    implicit: [{ type: 'evasion', value: 15, tier: 1 }],
    enhancement: 0,
  },

  // Boots
  'cizme': {
    name: 'Çizme',
    slot: 'boots',
    level: 1,
    implicit: [{ type: 'moveSpeed', value: 5, tier: 1 }],
    enhancement: 0,
  },

  // Gloves
  'eldiven': {
    name: 'Eldiven',
    slot: 'gloves',
    level: 1,
    implicit: [{ type: 'attackSpeed', value: 3, tier: 1 }],
    enhancement: 0,
  },

  // Belt
  'kemer': {
    name: 'Kemer',
    slot: 'belt',
    level: 1,
    implicit: [{ type: 'maxHp', value: 20, tier: 1 }],
    enhancement: 0,
  },

  // Amulet
  'muska': {
    name: 'Muska',
    slot: 'amulet',
    level: 1,
    implicit: [{ type: 'allResist', value: 5, tier: 1 }],
    enhancement: 0,
  },

  // Ring
  'yuzuk': {
    name: 'Yüzük',
    slot: 'ring1',
    level: 1,
    implicit: [],
    enhancement: 0,
  },

  // Capes
  'yun_pelerin': {
    name: 'Yün Pelerin',
    slot: 'cape',
    level: 1,
    implicit: [{ type: 'armor', value: 5, tier: 1 }],
    enhancement: 0,
  },
  'ipek_pelerin': {
    name: 'İpek Pelerin',
    slot: 'cape',
    level: 10,
    implicit: [{ type: 'evasion', value: 15, tier: 2 }],
    enhancement: 0,
  },
  'savas_pelerini': {
    name: 'Savaş Pelerini',
    slot: 'cape',
    level: 20,
    implicit: [{ type: 'armor', value: 20, tier: 2 }, { type: 'maxHp', value: 25, tier: 2 }],
    enhancement: 0,
  },

  // More Weapons
  'uzun_kilic': {
    name: 'Uzun Kılıç',
    slot: 'weapon',
    level: 15,
    implicit: [{ type: 'damage', value: 25, tier: 2 }],
    enhancement: 0,
  },
  'savas_baltasi': {
    name: 'Savaş Baltası',
    slot: 'weapon',
    level: 20,
    implicit: [{ type: 'damage', value: 35, tier: 3 }, { type: 'critChance', value: 5, tier: 1 }],
    enhancement: 0,
  },
  'kompozit_yay': {
    name: 'Kompozit Yay',
    slot: 'weapon',
    level: 15,
    implicit: [{ type: 'damage', value: 18, tier: 2 }, { type: 'attackSpeed', value: 15, tier: 2 }],
    enhancement: 0,
  },
  'kam_asasi': {
    name: 'Kam Asası',
    slot: 'weapon',
    level: 15,
    implicit: [{ type: 'spellDamage', value: 30, tier: 2 }],
    enhancement: 0,
  },

  // More Armor
  'celik_zirh': {
    name: 'Çelik Zırh',
    slot: 'chest',
    level: 20,
    implicit: [{ type: 'armor', value: 60, tier: 3 }],
    enhancement: 0,
  },
  'sert_deri': {
    name: 'Sert Deri Zırh',
    slot: 'chest',
    level: 15,
    implicit: [{ type: 'armor', value: 30, tier: 2 }, { type: 'evasion', value: 20, tier: 2 }],
    enhancement: 0,
  },

  // More Helmets
  'celik_tolga': {
    name: 'Çelik Tolga',
    slot: 'head',
    level: 20,
    implicit: [{ type: 'armor', value: 30, tier: 3 }],
    enhancement: 0,
  },
  'avcı_sapkasi': {
    name: 'Avcı Şapkası',
    slot: 'head',
    level: 10,
    implicit: [{ type: 'evasion', value: 25, tier: 2 }],
    enhancement: 0,
  },

  // More Boots
  'savas_cizmesi': {
    name: 'Savaş Çizmesi',
    slot: 'boots',
    level: 15,
    implicit: [{ type: 'moveSpeed', value: 10, tier: 2 }, { type: 'armor', value: 15, tier: 2 }],
    enhancement: 0,
  },
  'hafif_cizme': {
    name: 'Hafif Çizme',
    slot: 'boots',
    level: 10,
    implicit: [{ type: 'moveSpeed', value: 15, tier: 2 }, { type: 'evasion', value: 10, tier: 1 }],
    enhancement: 0,
  },

  // More Gloves
  'savas_eldiveni': {
    name: 'Savaş Eldiveni',
    slot: 'gloves',
    level: 15,
    implicit: [{ type: 'attackSpeed', value: 8, tier: 2 }, { type: 'damage', value: 5, tier: 1 }],
    enhancement: 0,
  },
  'deri_eldiven': {
    name: 'Deri Eldiven',
    slot: 'gloves',
    level: 5,
    implicit: [{ type: 'evasion', value: 10, tier: 1 }],
    enhancement: 0,
  },

  // ========================
  // Set Items - Alp Er Tunga Seti (Warrior)
  // ========================
  'set_alp_ertunga_kilic': {
    name: 'Alp Er Tunga Kılıcı',
    slot: 'weapon',
    level: 15,
    implicit: [{ type: 'damage', value: 25, tier: 3 }],
    enhancement: 0,
    setId: 'alp_er_tunga',
  },
  'set_alp_ertunga_demir_zirh': {
    name: 'Alp Er Tunga Zırhı',
    slot: 'chest',
    level: 15,
    implicit: [{ type: 'armor', value: 40, tier: 3 }],
    enhancement: 0,
    setId: 'alp_er_tunga',
  },
  'set_alp_ertunga_demir_tolgasi': {
    name: 'Alp Er Tunga Tolgası',
    slot: 'head',
    level: 15,
    implicit: [{ type: 'armor', value: 20, tier: 3 }],
    enhancement: 0,
    setId: 'alp_er_tunga',
  },
  'set_alp_ertunga_demir_eldiveni': {
    name: 'Alp Er Tunga Eldiveni',
    slot: 'gloves',
    level: 15,
    implicit: [{ type: 'attackSpeed', value: 10, tier: 2 }, { type: 'damage', value: 8, tier: 2 }],
    enhancement: 0,
    setId: 'alp_er_tunga',
  },

  // ========================
  // Set Items - Asena Seti (Börü)
  // ========================
  'set_asena_silah': {
    name: 'Asena Pençesi',
    slot: 'weapon',
    level: 20,
    implicit: [{ type: 'damage', value: 22, tier: 3 }, { type: 'attackSpeed', value: 15, tier: 2 }],
    enhancement: 0,
    setId: 'asena',
  },
  'set_asena_cizme': {
    name: 'Asena Çizmesi',
    slot: 'boots',
    level: 20,
    implicit: [{ type: 'moveSpeed', value: 15, tier: 3 }, { type: 'evasion', value: 15, tier: 2 }],
    enhancement: 0,
    setId: 'asena',
  },
  'set_asena_eldiven': {
    name: 'Asena Eldiveni',
    slot: 'gloves',
    level: 20,
    implicit: [{ type: 'attackSpeed', value: 12, tier: 3 }, { type: 'critChance', value: 5, tier: 1 }],
    enhancement: 0,
    setId: 'asena',
  },
  'set_asena_muska': {
    name: 'Asena Muskası',
    slot: 'amulet',
    level: 20,
    implicit: [{ type: 'allResist', value: 8, tier: 2 }, { type: 'agility', value: 5, tier: 1 }],
    enhancement: 0,
    setId: 'asena',
  },

  // ========================
  // Set Items - Kam Bilgeliği Seti (Shaman)
  // ========================
  'set_kam_bilgeligi_asa': {
    name: 'Kam Bilgeliği Asası',
    slot: 'weapon',
    level: 20,
    implicit: [{ type: 'spellDamage', value: 35, tier: 3 }],
    enhancement: 0,
    setId: 'kam_bilgeligi',
  },
  'set_kam_bilgeligi_cubbe': {
    name: 'Kam Bilgeliği Cübbesi',
    slot: 'chest',
    level: 20,
    implicit: [{ type: 'energyShield', value: 30, tier: 3 }, { type: 'maxMana', value: 25, tier: 2 }],
    enhancement: 0,
    setId: 'kam_bilgeligi',
  },
  'set_kam_bilgeligi_tolga': {
    name: 'Kam Bilgeliği Başlığı',
    slot: 'head',
    level: 20,
    implicit: [{ type: 'energyShield', value: 20, tier: 2 }, { type: 'spirit', value: 5, tier: 1 }],
    enhancement: 0,
    setId: 'kam_bilgeligi',
  },
  'set_kam_bilgeligi_yuzuk': {
    name: 'Kam Bilgeliği Yüzüğü',
    slot: 'ring1',
    level: 20,
    implicit: [{ type: 'manaRegen', value: 3, tier: 2 }, { type: 'spirit', value: 4, tier: 1 }],
    enhancement: 0,
    setId: 'kam_bilgeligi',
  },

  // ========================
  // Set Items - Göktürk Savaşçısı Seti (General)
  // ========================
  'set_gokturk_silah': {
    name: 'Göktürk Kılıcı',
    slot: 'weapon',
    level: 25,
    implicit: [{ type: 'damage', value: 30, tier: 3 }],
    enhancement: 0,
    setId: 'gokturk',
  },
  'set_gokturk_zirh': {
    name: 'Göktürk Zırhı',
    slot: 'chest',
    level: 25,
    implicit: [{ type: 'armor', value: 45, tier: 3 }, { type: 'maxHp', value: 30, tier: 2 }],
    enhancement: 0,
    setId: 'gokturk',
  },
  'set_gokturk_cizme': {
    name: 'Göktürk Çizmesi',
    slot: 'boots',
    level: 25,
    implicit: [{ type: 'moveSpeed', value: 10, tier: 2 }, { type: 'armor', value: 15, tier: 2 }],
    enhancement: 0,
    setId: 'gokturk',
  },
  'set_gokturk_kemer': {
    name: 'Göktürk Kemeri',
    slot: 'belt',
    level: 25,
    implicit: [{ type: 'maxHp', value: 35, tier: 3 }, { type: 'strength', value: 5, tier: 1 }],
    enhancement: 0,
    setId: 'gokturk',
  },

  // ========================
  // Set Items - Bozkır Okçusu Seti (Archer)
  // ========================
  'set_bozkir_yay': {
    name: 'Bozkır Yayı',
    slot: 'weapon',
    level: 20,
    implicit: [{ type: 'damage', value: 20, tier: 3 }, { type: 'attackSpeed', value: 18, tier: 3 }],
    enhancement: 0,
    setId: 'bozkir',
  },
  'set_bozkir_zirh': {
    name: 'Bozkır Okçu Zırhı',
    slot: 'chest',
    level: 20,
    implicit: [{ type: 'evasion', value: 25, tier: 3 }, { type: 'armor', value: 15, tier: 2 }],
    enhancement: 0,
    setId: 'bozkir',
  },
  'set_bozkir_eldiven': {
    name: 'Bozkır Okçu Eldiveni',
    slot: 'gloves',
    level: 20,
    implicit: [{ type: 'attackSpeed', value: 10, tier: 2 }, { type: 'agility', value: 5, tier: 1 }],
    enhancement: 0,
    setId: 'bozkir',
  },
  'set_bozkir_cizme': {
    name: 'Bozkır Okçu Çizmesi',
    slot: 'boots',
    level: 20,
    implicit: [{ type: 'moveSpeed', value: 12, tier: 2 }, { type: 'evasion', value: 15, tier: 2 }],
    enhancement: 0,
    setId: 'bozkir',
  },

  // ========================
  // Set Items - Ruh Yürüyücü Seti (Endgame)
  // ========================
  'set_ruh_yuruyucu_muska': {
    name: 'Ruh Yürüyücü Muskası',
    slot: 'amulet',
    level: 35,
    implicit: [{ type: 'allResist', value: 12, tier: 3 }, { type: 'maxHp', value: 40, tier: 3 }],
    enhancement: 0,
    setId: 'ruh_yuruyucu',
  },
  'set_ruh_yuruyucu_yuzuk': {
    name: 'Ruh Yürüyücü Yüzüğü',
    slot: 'ring1',
    level: 35,
    implicit: [{ type: 'damage', value: 15, tier: 3, calcType: 'increased' }, { type: 'spirit', value: 8, tier: 2 }],
    enhancement: 0,
    setId: 'ruh_yuruyucu',
  },
  'set_ruh_yuruyucu_pelerin': {
    name: 'Ruh Yürüyücü Pelerini',
    slot: 'cape',
    level: 35,
    implicit: [{ type: 'evasion', value: 25, tier: 3 }, { type: 'maxHp', value: 30, tier: 2 }],
    enhancement: 0,
    setId: 'ruh_yuruyucu',
  },
};

// Mod pools
// calcType: 'flat' = direct add, 'increased' = additive %, 'more' = multiplicative %
export const PREFIX_MODS: ItemMod[] = [
  // Flat life
  { type: 'maxHp', value: 20, tier: 1 },
  { type: 'maxHp', value: 35, tier: 2 },
  { type: 'maxHp', value: 50, tier: 3 },
  // % increased life
  { type: 'maxHp', value: 5, tier: 1, calcType: 'increased' },
  { type: 'maxHp', value: 8, tier: 2, calcType: 'increased' },
  { type: 'maxHp', value: 12, tier: 3, calcType: 'increased' },
  // Flat mana
  { type: 'maxMana', value: 15, tier: 1 },
  { type: 'maxMana', value: 25, tier: 2 },
  { type: 'maxMana', value: 40, tier: 3 },
  // Flat armor
  { type: 'armor', value: 15, tier: 1 },
  { type: 'armor', value: 30, tier: 2 },
  { type: 'armor', value: 50, tier: 3 },
  // % increased armor
  { type: 'armor', value: 10, tier: 1, calcType: 'increased' },
  { type: 'armor', value: 20, tier: 2, calcType: 'increased' },
  { type: 'armor', value: 35, tier: 3, calcType: 'increased' },
  // Flat damage
  { type: 'damage', value: 5, tier: 1 },
  { type: 'damage', value: 10, tier: 2 },
  { type: 'damage', value: 18, tier: 3 },
  // % increased damage
  { type: 'damage', value: 10, tier: 1, calcType: 'increased' },
  { type: 'damage', value: 18, tier: 2, calcType: 'increased' },
  { type: 'damage', value: 28, tier: 3, calcType: 'increased' },
  // Energy shield
  { type: 'energyShield', value: 15, tier: 1 },
  { type: 'energyShield', value: 30, tier: 2 },
  // Evasion (flat)
  { type: 'evasion', value: 12, tier: 1 },
  { type: 'evasion', value: 25, tier: 2 },
  { type: 'evasion', value: 40, tier: 3 },
  // % increased evasion
  { type: 'evasion', value: 10, tier: 1, calcType: 'increased' },
  { type: 'evasion', value: 20, tier: 2, calcType: 'increased' },
  // Physical damage (% increased)
  { type: 'physicalDamage', value: 12, tier: 1, calcType: 'increased' },
  { type: 'physicalDamage', value: 20, tier: 2, calcType: 'increased' },
  { type: 'physicalDamage', value: 30, tier: 3, calcType: 'increased' },
  // Elemental damage (% increased)
  { type: 'odDamage', value: 8, tier: 1, calcType: 'increased' },
  { type: 'odDamage', value: 15, tier: 2, calcType: 'increased' },
  { type: 'odDamage', value: 25, tier: 3, calcType: 'increased' },
  { type: 'suDamage', value: 8, tier: 1, calcType: 'increased' },
  { type: 'suDamage', value: 15, tier: 2, calcType: 'increased' },
  { type: 'suDamage', value: 25, tier: 3, calcType: 'increased' },
  { type: 'gokDamage', value: 8, tier: 1, calcType: 'increased' },
  { type: 'gokDamage', value: 15, tier: 2, calcType: 'increased' },
  { type: 'gokDamage', value: 25, tier: 3, calcType: 'increased' },
  // Karanlık damage (% increased)
  { type: 'karanlikDamage', value: 10, tier: 2, calcType: 'increased' },
  { type: 'karanlikDamage', value: 20, tier: 3, calcType: 'increased' },
  // Spell damage (% increased)
  { type: 'spellDamage', value: 10, tier: 1, calcType: 'increased' },
  { type: 'spellDamage', value: 18, tier: 2, calcType: 'increased' },
  { type: 'spellDamage', value: 28, tier: 3, calcType: 'increased' },
];

export const SUFFIX_MODS: ItemMod[] = [
  // Flat attributes
  { type: 'strength', value: 3, tier: 1 },
  { type: 'strength', value: 6, tier: 2 },
  { type: 'strength', value: 10, tier: 3 },
  { type: 'agility', value: 3, tier: 1 },
  { type: 'agility', value: 6, tier: 2 },
  { type: 'agility', value: 10, tier: 3 },
  { type: 'spirit', value: 3, tier: 1 },
  { type: 'spirit', value: 6, tier: 2 },
  { type: 'spirit', value: 10, tier: 3 },
  // Crit (% increased)
  { type: 'critChance', value: 10, tier: 1, calcType: 'increased' },
  { type: 'critChance', value: 18, tier: 2, calcType: 'increased' },
  { type: 'critChance', value: 28, tier: 3, calcType: 'increased' },
  // Crit damage (% increased)
  { type: 'critDamage', value: 10, tier: 1, calcType: 'increased' },
  { type: 'critDamage', value: 18, tier: 2, calcType: 'increased' },
  // Movement speed (% increased)
  { type: 'moveSpeed', value: 5, tier: 1, calcType: 'increased' },
  { type: 'moveSpeed', value: 10, tier: 2, calcType: 'increased' },
  // Attack speed (% increased)
  { type: 'attackSpeed', value: 5, tier: 1, calcType: 'increased' },
  { type: 'attackSpeed', value: 10, tier: 2, calcType: 'increased' },
  { type: 'attackSpeed', value: 15, tier: 3, calcType: 'increased' },
  // Resistances
  { type: 'odResist', value: 10, tier: 1 },
  { type: 'odResist', value: 18, tier: 2 },
  { type: 'suResist', value: 10, tier: 1 },
  { type: 'suResist', value: 18, tier: 2 },
  { type: 'gokResist', value: 10, tier: 1 },
  { type: 'gokResist', value: 18, tier: 2 },
  { type: 'karanlikResist', value: 8, tier: 1 },
  { type: 'karanlikResist', value: 15, tier: 2 },
  { type: 'allResist', value: 5, tier: 2 },
  { type: 'allResist', value: 8, tier: 3 },
  // HP regen
  { type: 'hpRegen', value: 2, tier: 1 },
  { type: 'hpRegen', value: 5, tier: 2 },
  { type: 'hpRegen', value: 10, tier: 3 },
  // Mana regen
  { type: 'manaRegen', value: 1, tier: 1 },
  { type: 'manaRegen', value: 3, tier: 2 },
  { type: 'manaRegen', value: 6, tier: 3 },
  // Life leech
  { type: 'lifeLeech', value: 0.5, tier: 2 },
  { type: 'lifeLeech', value: 1, tier: 3 },
  // Mana leech
  { type: 'manaLeech', value: 0.3, tier: 2 },
  { type: 'manaLeech', value: 0.6, tier: 3 },
  // Cast speed (% increased)
  { type: 'castSpeed', value: 5, tier: 1, calcType: 'increased' },
  { type: 'castSpeed', value: 10, tier: 2, calcType: 'increased' },
  // Cooldown reduction
  { type: 'cooldownReduction', value: 3, tier: 2 },
  { type: 'cooldownReduction', value: 5, tier: 3 },
  // Block chance
  { type: 'blockChance', value: 3, tier: 1 },
  { type: 'blockChance', value: 5, tier: 2 },
  { type: 'blockChance', value: 8, tier: 3 },
];

// Generate visual properties based on slot, rarity and item level
function generateVisual(slot: EquipmentSlot, rarity: ItemRarity, itemLevel: number): EquipmentVisual {
  // Base style based on slot type
  let style: EquipmentVisual['style'] = 'leather';
  if (slot === 'weapon' || slot === 'offhand') {
    style = itemLevel >= 20 ? 'plate' : 'chain';
  } else if (slot === 'chest' || slot === 'head') {
    style = itemLevel >= 30 ? 'plate' : itemLevel >= 15 ? 'chain' : 'leather';
  } else if (slot === 'cape') {
    style = rarity === 'legendary' ? 'magical' : 'cloth';
  }

  // Override for high rarity
  if (rarity === 'legendary') {
    style = 'legendary';
  } else if (rarity === 'set') {
    style = 'magical';
  }

  // Color palettes based on rarity
  const colorPalettes: Record<ItemRarity, { primary: string; secondary: string; accent: string }[]> = {
    normal: [
      { primary: '#8B7355', secondary: '#6B5344', accent: '#9B8365' },  // Brown leather
      { primary: '#707070', secondary: '#505050', accent: '#909090' },  // Gray metal
    ],
    magic: [
      { primary: '#4466AA', secondary: '#335588', accent: '#6688CC' },  // Blue
      { primary: '#44AA66', secondary: '#338855', accent: '#66CC88' },  // Green
    ],
    rare: [
      { primary: '#AA8844', secondary: '#886633', accent: '#CCAA66' },  // Gold
      { primary: '#AA4466', secondary: '#883355', accent: '#CC6688' },  // Red
    ],
    set: [
      { primary: '#228844', secondary: '#116633', accent: '#44AA66' },  // Green set
      { primary: '#44AAAA', secondary: '#338888', accent: '#66CCCC' },  // Cyan
    ],
    legendary: [
      { primary: '#FF8800', secondary: '#CC6600', accent: '#FFAA44' },  // Orange flame
      { primary: '#AA00FF', secondary: '#8800CC', accent: '#CC44FF' },  // Violet
      { primary: '#00FFAA', secondary: '#00CC88', accent: '#44FFCC' },  // Emerald
    ],
  };

  const palettes = colorPalettes[rarity];
  const palette = palettes[Math.floor(Math.random() * palettes.length)];

  const visual: EquipmentVisual = {
    primaryColor: palette.primary,
    secondaryColor: palette.secondary,
    accentColor: palette.accent,
    style,
  };

  // Add glow for rare+ items
  if (rarity === 'rare') {
    visual.glow = '#FFDD44';
  } else if (rarity === 'set') {
    visual.glow = '#2ecc71';
  } else if (rarity === 'legendary') {
    visual.glow = '#FF8800';
  }

  return visual;
}

// Item generation functions
export function createEquipmentItem(
  baseId: string,
  rarity: ItemRarity,
  itemLevel: number
): EquipmentItem | null {
  // Try new registry first, then legacy EQUIPMENT_BASES
  const registryBase = getBaseById(baseId);
  const legacyBase = EQUIPMENT_BASES[baseId];

  if (!registryBase && !legacyBase) return null;

  let item: EquipmentItem;

  if (registryBase) {
    // New registry path
    item = {
      id: generateItemId(),
      name: registryBase.name,
      baseName: registryBase.name,
      slot: registryBase.slot,
      level: Math.max(registryBase.level, itemLevel),
      rarity,
      implicit: registryBase.implicit.map(m => ({ ...m })),
      prefixes: [],
      suffixes: [],
      enhancement: 0,
      visual: generateVisual(registryBase.slot, rarity, itemLevel),
      icon: getItemIconPath(baseId, rarity) || SLOT_FALLBACK_ICONS[registryBase.slot],
      // New fields
      baseId: registryBase.id,
      category: registryBase.category,
      gridSize: { ...registryBase.gridSize },
      defenseType: registryBase.defenseType,
      weaponSubType: registryBase.weaponSubType,
      ilvl: itemLevel,
    };
  } else {
    // Legacy path (for set items and old base IDs)
    const base = legacyBase!;
    item = {
      id: generateItemId(),
      name: base.name,
      baseName: base.name,
      slot: base.slot,
      level: Math.max(base.level, itemLevel),
      rarity,
      implicit: [...base.implicit],
      prefixes: [],
      suffixes: [],
      enhancement: 0,
      visual: generateVisual(base.slot, rarity, itemLevel),
      icon: getItemIconPath(baseId, rarity) || SLOT_FALLBACK_ICONS[base.slot],
      ...(base.setId ? { setId: base.setId } : {}),
      // Add new fields with defaults for legacy items
      baseId: LEGACY_BASE_MAP[baseId] || baseId,
      category: getCategoryForSlot(base.slot),
      gridSize: getDefaultGridSize(base.slot),
      ilvl: itemLevel,
    };
  }

  // Add mods based on rarity using new slot-aware system
  const modCounts = getModCountForRarity(rarity);
  const { prefixes, suffixes } = rollModsForItem(item, modCounts.prefixes, modCounts.suffixes, itemLevel);
  item.prefixes = prefixes;
  item.suffixes = suffixes;

  // Update name based on rarity
  if (rarity !== 'normal') {
    item.name = generateItemName(item);
  }

  return item;
}

/**
 * Create equipment item from a registry base definition directly.
 * Used by LootTable for ilvl-based base selection.
 */
export function createEquipmentFromBase(
  base: BaseItemDefinition,
  rarity: ItemRarity,
  itemLevel: number
): EquipmentItem {
  const item: EquipmentItem = {
    id: generateItemId(),
    name: base.name,
    baseName: base.name,
    slot: base.slot,
    level: Math.max(base.level, itemLevel),
    rarity,
    implicit: base.implicit.map(m => ({ ...m })),
    prefixes: [],
    suffixes: [],
    enhancement: 0,
    visual: generateVisual(base.slot, rarity, itemLevel),
    icon: getItemIconPath(base.id, rarity) || SLOT_FALLBACK_ICONS[base.slot],
    baseId: base.id,
    category: base.category,
    gridSize: { ...base.gridSize },
    defenseType: base.defenseType,
    weaponSubType: base.weaponSubType,
    ilvl: itemLevel,
    ...(base.setId ? { setId: base.setId } : {}),
  };

  const modCounts = getModCountForRarity(rarity);
  const { prefixes, suffixes } = rollModsForItem(item, modCounts.prefixes, modCounts.suffixes, itemLevel);
  item.prefixes = prefixes;
  item.suffixes = suffixes;

  if (rarity !== 'normal') {
    item.name = generateItemName(item);
  }

  return item;
}

function getModCountForRarity(rarity: ItemRarity): { prefixes: number; suffixes: number } {
  switch (rarity) {
    case 'normal':
      return { prefixes: 0, suffixes: 0 };
    case 'magic':
      return { prefixes: 1, suffixes: 1 };
    case 'rare':
      return { prefixes: 2, suffixes: 2 };
    case 'set':
      return { prefixes: 3, suffixes: 2 };
    case 'legendary':
      return { prefixes: 3, suffixes: 3 };
    default:
      return { prefixes: 0, suffixes: 0 };
  }
}

function generateItemName(item: EquipmentItem): string {
  let prefix = '';
  let suffix = '';

  if (item.prefixes.length > 0) {
    const mod = item.prefixes[0];
    const p = getModNamePrefix(mod.type, mod.calcType);
    if (p) prefix = p + ' ';
  }

  if (item.suffixes.length > 0) {
    const mod = item.suffixes[0];
    const s = getModNameSuffix(mod.type, mod.calcType);
    if (s) suffix = ' ' + s;
  }

  // Use stored baseName for reliable name generation (survives multiple crafting operations)
  const baseName = item.baseName || item.name;
  return `${prefix}${baseName}${suffix}`;
}

export function createCurrencyDrop(amount: number): CurrencyItem {
  return {
    id: generateItemId(),
    name: 'Altun',
    rarity: 'normal',
    level: 1,
    icon: 'sprites/items/icons_7soul/I_GoldCoin.png',
    itemType: 'currency',
    currencyType: 'altun',
    stackSize: amount,
    maxStack: 9999,
  };
}

export function getItemColor(rarity: ItemRarity): string {
  return ITEM.COLORS[rarity];
}

// ========================
// Quest Items
// ========================

export interface QuestItem extends ItemBase {
  itemType: 'quest';
  questId: string;        // Which quest this item is for
  stackSize: number;
  maxStack: number;
  description: string;
}

export const QUEST_ITEMS: Record<string, QuestItem> = {
  ruh_damlasi: {
    id: '',
    name: 'Ruh Damlası',
    rarity: 'rare',
    level: 1,
    itemType: 'quest',
    questId: 'side_ruh_damlaları',
    stackSize: 1,
    maxStack: 10,
    description: 'Karabasanlardan düşen esrarengiz bir özüt. Şifalı özellikleri var.',
  },
  erlik_idolu: {
    id: '',
    name: 'Erlik İdolü',
    rarity: 'rare',
    level: 1,
    itemType: 'quest',
    questId: 'side_kara_gulle_bilmecesi',
    stackSize: 1,
    maxStack: 1,
    description: 'Karanlık tanrı Erlik\'i tasvir eden küçük bir idol. Kötücül bir enerji yayıyor.',
  },
};

export function createQuestItem(questItemId: string): QuestItem | null {
  const template = QUEST_ITEMS[questItemId];
  if (!template) return null;

  return {
    ...template,
    id: generateItemId(),
  };
}

// ========================
// Quest Reward Items (Pre-defined unique equipment)
// ========================

export const QUEST_REWARD_ITEMS: Record<string, EquipmentItem> = {
  ruh_yuzugu: {
    id: '',
    name: 'Ruh Yüzüğü',
    icon: 'sprites/items/icons_7soul/Ac_Ring02.png',
    slot: 'ring1',
    level: 8,
    rarity: 'magic',
    implicit: [],
    prefixes: [{ type: 'spirit', value: 5, tier: 2 }],
    suffixes: [{ type: 'maxMana', value: 15, tier: 1 }],
    enhancement: 0,
    visual: {
      primaryColor: '#4466AA',
      secondaryColor: '#335588',
      accentColor: '#6688CC',
      style: 'magical',
      glow: '#8888ff',
    },
  },
  kara_gulle_tilsimi: {
    id: '',
    name: 'Kara Gülle\'nin Tılsımı',
    icon: 'sprites/items/icons_7soul/Ac_Necklace05.png',
    slot: 'amulet',
    level: 10,
    rarity: 'rare',
    implicit: [{ type: 'allResist', value: 5, tier: 1 }],
    prefixes: [{ type: 'damage', value: 10, tier: 2, calcType: 'increased' }],
    suffixes: [{ type: 'spirit', value: 4, tier: 1 }],
    enhancement: 0,
    visual: {
      primaryColor: '#AA4466',
      secondaryColor: '#883355',
      accentColor: '#CC6688',
      style: 'magical',
      glow: '#FFDD44',
    },
  },
  albasti_taci: {
    id: '',
    name: 'Albastı Tacı',
    icon: 'sprites/items/icons_7soul/C_Elm04.png',
    slot: 'head',
    level: 12,
    rarity: 'legendary',
    implicit: [{ type: 'armor', value: 20, tier: 3 }],
    prefixes: [
      { type: 'maxHp', value: 20, tier: 2 },
      { type: 'spirit', value: 10, tier: 3 },
    ],
    suffixes: [
      { type: 'critChance', value: 5, tier: 1, calcType: 'increased' },
    ],
    enhancement: 0,
    visual: {
      primaryColor: '#FF8800',
      secondaryColor: '#CC6600',
      accentColor: '#FFAA44',
      style: 'legendary',
      glow: '#FF8800',
    },
  },
};

export function createQuestRewardItem(rewardItemId: string): EquipmentItem | null {
  const template = QUEST_REWARD_ITEMS[rewardItemId];
  if (!template) return null;

  return {
    ...template,
    id: generateItemId(),
  };
}
